import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminFailedJob, AdminJob, AdminJobsView } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Chip } from "@/ui/Chip";
import { Button } from "@/ui/Button";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { ReasonDialog } from "@/features/admin/ReasonDialog";
import { absoluteTime, cadence, duration, relativeTime, shortTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/jobs")({
  component: JobsScreen,
});

/**
 * The platform's own machinery.
 *
 * # WHY THIS SCREEN EXISTS
 *
 * Nine periodic jobs carry Drumlin, and none of them was visible anywhere. The
 * two that matter most fail silently by construction:
 *
 *   the meter turns running services into chargeable hours, so if it stops,
 *     revenue stops and no error reaches anybody — nothing a customer does is
 *     involved;
 *   certificate renewal keeps every custom domain answering, so if it stops,
 *     all of them expire about thirty days later, together.
 *
 * Both were discovered by a customer writing in, which is the worst available
 * detector.
 */
function JobsScreen() {
  const [retrying, setRetrying] = useState<AdminFailedJob | null>(null);

  const jobs = useQuery({
    queryKey: ["platform-jobs"],
    queryFn: ({ signal }) => api.get<AdminJobsView>("/admin/jobs", undefined, signal),
    refetchInterval: 20000,
  });

  const failed = useQuery({
    queryKey: ["platform-jobs-failed"],
    queryFn: ({ signal }) =>
      api.get<{ jobs: AdminFailedJob[] | null }>(
        "/admin/jobs/failed",
        { limit: 50 },
        signal,
      ),
    refetchInterval: 20000,
  });

  if (jobs.isError) {
    return <ErrorState error={jobs.error} onRetry={() => void jobs.refetch()} />;
  }

  const view = jobs.data;
  const list = view?.jobs ?? [];
  const broken = failed.data?.jobs ?? [];
  const overdue = list.filter((j) => j.overdue);
  // Separate from overdue, and deliberately so. A job that has exhausted its
  // attempts is not late — it has stopped, permanently, and nothing will pick
  // it up again. A six-hourly sweep that gave up an hour ago is well inside its
  // window and still needs somebody: judging it by the clock alone would hide
  // the worst state the machinery has.
  const givenUp = list.filter((j) => j.discarded > 0);

  return (
    <div className="flex flex-col gap-4">
      {/* The three things to say before the table, in the order they matter. */}
      {view && !view.worker_last_seen && (
        <Banner
          tone="danger"
          title="No worker has ever run against this database"
          body="Nothing is metering usage, renewing certificates, verifying domains or delivering notifications. The worker writes its schedule down when it starts, and there is nothing written."
        />
      )}
      {view?.worker_last_seen && overdue.length > 0 && (
        <Banner
          tone="danger"
          title={
            overdue.length === 1
              ? `${overdue[0]?.kind ?? "A job"} has missed a run`
              : `${overdue.length} jobs have missed a run`
          }
          body={overdue.map((j) => j.kind).join(", ")}
        />
      )}
      {view?.worker_last_seen && givenUp.length > 0 && (
        <Banner
          tone="danger"
          title={
            givenUp.length === 1
              ? `${givenUp[0]?.kind ?? "A job"} has run out of attempts`
              : `${givenUp.length} jobs have run out of attempts`
          }
          body={`Nothing will try these again without being asked: ${givenUp
            .map((j) => `${j.kind} (${j.discarded})`)
            .join(", ")}.`}
        />
      )}
      {view && view.worker_last_seen && !view.history_available && (
        <Banner
          tone="warning"
          title="There is no job history here"
          body="A worker has registered a schedule but the queue's own tables are missing, so what ran cannot be read."
        />
      )}

      {/* The two sections below describe what a running worker is doing.
          With no worker they say the same thing the banner above already
          said, in two more boxes: "No schedule is registered" and "Nothing
          is failing", which together read as reassurance on a screen whose
          headline is that nothing is running at all. */}
      {view?.worker_last_seen && (
      <Panel
        title="Scheduled work"
        description={
          view?.worker_last_seen
            ? `A worker last declared this schedule ${relativeTime(view.worker_last_seen)}. Finished jobs are cleaned up after ${cadence(view.retention_seconds)}, so a job with no recorded success inside that window is a job that has not run.`
            : "What the worker is meant to run, and what became of it."
        }
        padded={false}
      >
        {jobs.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={6} columns={6} />
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            title="No schedule is registered"
            description="The worker writes this down at start-up. Either none has started, or none registers periodic work."
          />
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[27%]">Job</Th>
                  <Th width="w-[7%]" numeric>
                    Every
                  </Th>
                  <Th width="w-[15%]">Last success</Th>
                  <Th width="w-[8%]" numeric>
                    Took
                  </Th>
                  <Th width="w-[18%]">Next</Th>
                  <Th width="w-[11%]" numeric>
                    In flight
                  </Th>
                  <Th width="w-[14%]">Failing</Th>
                </Tr>
              </thead>
              <tbody>
                {list.map((j) => (
                  <JobRow key={j.kind} job={j} historyAvailable={view?.history_available ?? false} />
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>

      )}

      {view?.worker_last_seen && (
      <Panel
        title="Failures"
        description="Retryable jobs will be tried again on their own. A discarded job has run out of attempts and nothing will pick it up without being asked."
        padded={false}
      >
        {failed.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={3} columns={5} />
          </div>
        ) : failed.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={failed.error} onRetry={() => void failed.refetch()} />
          </div>
        ) : broken.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState
              title="Nothing is failing"
              description="No job is waiting to be retried or has run out of attempts."
            />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[23%]">Job</Th>
                  <Th width="w-[12%]">State</Th>
                  <Th width="w-[9%]" numeric>
                    Attempt
                  </Th>
                  <Th width="w-[26%]">Error</Th>
                  <Th width="w-[14%]" align="right">
                    When
                  </Th>
                  <Th width="w-[16%]" align="right">
                    Retry
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {broken.map((j) => (
                  <Tr key={j.id}>
                    <Td clip title={j.args || undefined}>
                      {j.kind}
                    </Td>
                    <Td>
                      <Chip tone={j.state === "discarded" ? "down" : "warn"}>
                        {j.state === "discarded" ? "Given up" : "Will retry"}
                      </Chip>
                    </Td>
                    <Td numeric>
                      {j.attempt}/{j.max_attempts}
                    </Td>
                    <Td clip title={j.error || undefined}>
                      <span className="text-[var(--text-secondary)]">{j.error || "—"}</span>
                    </Td>
                    <Td
                      align="right"
                      title={absoluteTime(j.finalized_at ?? j.created_at)}
                    >
                      {shortTime(j.finalized_at ?? j.created_at)}
                    </Td>
                    <Td align="right">
                      <Button tone="secondary" size="dense" onClick={() => setRetrying(j)}>
                        Retry now
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>
      )}

      {retrying && (
        <ReasonDialog
          open
          onOpenChange={(open) => !open && setRetrying(null)}
          title={`Retry ${retrying.kind}`}
          description={
            retrying.state === "discarded"
              ? "This job has run out of attempts, so nothing will pick it up on its own. Retrying puts it back in the queue now."
              : "This job will be tried again on its own. Retrying puts it back in the queue now instead of waiting."
          }
          prompt="Why now"
          confirmLabel="Retry it"
          action={(reason) => api.post<void>(`/admin/jobs/${retrying.id}/retry`, { reason })}
          onDone={async () => {
            setRetrying(null);
            await Promise.all([jobs.refetch(), failed.refetch()]);
          }}
        />
      )}
    </div>
  );
}

function JobRow({ job, historyAvailable }: { job: AdminJob; historyAvailable: boolean }) {
  return (
    <Tr className={job.overdue ? "bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)]" : undefined}>
      <Td clip title={job.run_on_start ? "Also runs once at start-up" : undefined}>
        <span className={job.overdue ? "font-medium text-[var(--danger)]" : "font-medium"}>
          {job.kind}
        </span>
      </Td>
      <Td numeric>{cadence(job.interval_seconds)}</Td>
      <Td
        clip
        title={job.last_success_at ? absoluteTime(job.last_success_at) : undefined}
      >
        {job.last_success_at ? (
          <span className={job.overdue ? "text-[var(--danger)]" : undefined}>
            {shortTime(job.last_success_at)}
          </span>
        ) : (
          // Said as a sentence, not a dash. The absence is the finding.
          <span className="text-[var(--text-muted)]">
            {historyAvailable ? "none recorded" : "unknown"}
          </span>
        )}
      </Td>
      <Td numeric>
        {job.last_success_seconds === undefined ? "—" : duration(job.last_success_seconds)}
      </Td>
      <Td clip title={job.next_run_at ? absoluteTime(job.next_run_at) : undefined}>
        {job.next_run_at ? shortTime(job.next_run_at) : "—"}
      </Td>
      {/* Only the half that is non-zero. "0 running, 1 queued" did not fit the
          column and the zero was never the interesting part. */}
      <Td
        numeric
        title={`${job.running} running, ${job.waiting} queued`}
      >
        {job.running > 0
          ? `${job.running} running`
          : job.waiting > 0
            ? `${job.waiting} queued`
            : "—"}
      </Td>
      <Td>
        {job.retrying === 0 && job.discarded === 0 ? (
          <span className="text-[var(--text-muted)]">—</span>
        ) : (
          <span className="flex items-center gap-1">
            {job.retrying > 0 && <Chip tone="warn">{job.retrying} retrying</Chip>}
            {job.discarded > 0 && <Chip tone="down">{job.discarded} given up</Chip>}
          </span>
        )}
      </Td>
    </Tr>
  );
}

function Banner({
  tone,
  title,
  body,
}: {
  tone: "danger" | "warning";
  title: string;
  body: string;
}) {
  const colours =
    tone === "danger"
      ? "border-[var(--danger)] bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)] text-[var(--danger)]"
      : "border-[color-mix(in_oklab,_var(--warning)_35%,_transparent)] bg-[color-mix(in_oklab,_var(--warning)_12%,_transparent)] text-[var(--warning)]";
  return (
    <div className={`rounded-xl border px-4 py-3 ${colours}`}>
      <p className="text-[14px] font-medium">{title}</p>
      <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">{body}</p>
    </div>
  );
}
